import {useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {msg, t, Trans} from '@lingui/macro'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography, useTheme
} from '@mui/material'
import {GridActionsCellItem, GridRowEditStopReasons, GridRowModes} from '@mui/x-data-grid'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import {Check, Visibility, VisibilityOff} from '@mui/icons-material'
import {useLingui} from '@lingui/react'
import SvgCustomIcon from "../icons/SvgCustomIcon";
import {StripedDataGrid} from "./StripedDataGrid";

const paginationModel = { page: 0, pageSize: 25 }



export default function UsersTable ({ usersRows, modifyUser }) {
  const location = useLocation()
  const organizations = location.state?.init?.organizations || {}
  const user = location.state?.init?.user || {}
  const [rows, setRows] = useState([])
  const [rowModesModel, setRowModesModel] = useState({})
  const [dialogOpen, setDialogOpen] = useState(false)
  const [userId, setUserId] = useState(0)
  const theme = useTheme()
  const { _ } = useLingui()

  let typeOptions = []
  if (user.type === 1) {
    typeOptions = [
      { label: t`User`, value: 0 }
      , { label: t`Admin`, value: 1 }
      , { label: t`Organization admin`, value: 2 }
    ]
  }
  if (user.type === 2) {
    typeOptions = [
      { label: t`User`, value: 0 }
      , { label: t`Organization admin`, value: 2 }
    ]
  }

  useEffect(() => {
    if (usersRows.length) setRows(usersRows)
  }, [usersRows])

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const handleEditClick = id => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveClick = id => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelClick = id => () => {
    setRowModesModel({
      ...rowModesModel
      , [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })

    const editedRow = rows.find(row => row.id === id)
    if (editedRow.isNew) {
      setRows(rows.filter(row => row.id !== id))
    }
  }

  const PasswordChangeDialog = ({ id, open, onClose }) => {
    const passwordRef = useRef()
    const confirmPasswordRef = useRef()
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [error, setError] = useState('')

    const handleMouseDownPassword = event => { event.preventDefault() }

    const handleSubmit = e => {
      e.preventDefault()

      // Basic validation
      if (newPassword !== confirmPassword) {
        setError('Passwords must match')
        return
      }

      if (newPassword.length < 8) {
        setError('Password must be at least 8 characters long')
        return
      }

      if (!/(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])./.test(newPassword)) {
        setError(_(msg`Password must contain at least a lowercase letter, an upper case letter and a digit`))
        return
      }

      // Call API to change the password
      // console.log('New Password:', newPassword)
      modifyUser({ id, password: newPassword })

      // Reset form and close dialog
      setNewPassword('')
      setConfirmPassword('')
      setError('')
      onClose()
    }

    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle><Trans>Change Password</Trans></DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                inputRef={passwordRef}
                label="New Password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                fullWidth
                type={passwordRef.current?.type || 'password'}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={() => {
                      passwordRef.current.type === 'text'
                        ? (passwordRef.current.type = 'password')
                        : (passwordRef.current.type = 'text')
                    }}
                    onMouseDown={handleMouseDownPassword}
                    edge='end'
                  >
                    {passwordRef.current?.type === 'text' ? <VisibilityOff color={theme.palette.newColors.gray} /> : <Visibility color={theme.palette.newColors.gray} />}
                  </IconButton>
                </InputAdornment>
                }}
              />
              <TextField
                inputRef={confirmPasswordRef}
                label="Confirm New Password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                fullWidth
                type={confirmPasswordRef.current?.type || 'password'}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={() => {
                        confirmPasswordRef.current.type === 'text'
                          ? confirmPasswordRef.current.type = 'password'
                          : confirmPasswordRef.current.type = 'text'
                      }}
                      onMouseDown={handleMouseDownPassword}
                      edge='end'
                    >
                      {confirmPasswordRef.current?.type === 'text' ? <VisibilityOff color={theme.palette.newColors.gray} /> : <Visibility color={theme.palette.newColors.gray} />}
                    </IconButton>
                  </InputAdornment>
                }}
              />
              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
            </Stack>
          </form>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='error' onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type="submit" variant="outlined" color="success" onClick={handleSubmit}>
            <Trans>Change Password</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const handleOpenDialog = id => () => {
    setUserId(id)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => setDialogOpen(false)

  const processRowUpdate = (newRow, oldRow) => {
    // console.log('NEW', newRow, 'OLD', oldRow)
    let updatedRow = { isNew: false, id: newRow.id }
    Object.keys(newRow).forEach(key => {
      if (newRow[key] !== oldRow[key]) {
        updatedRow = { ...updatedRow, [key]: newRow[key] }
      }
    })
    modifyUser(updatedRow)
    setRows(rows.map(row => (row.id === newRow.id ? newRow : row)))
    return updatedRow
  }

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel)
  }

  const columns = [
    {
      field             : 'id'
      , headerName      : t`ID`
      , headerClassName : 'overload-theme--header'
      , flex            : 0.5 // , minWidth: 50
    }
    , {
      field             : 'username'
      , headerName      : t`Username`
      , headerClassName : 'overload-theme--header'
      , flex            : 1.5 // , minWidth: 150
    }
    , {
      field             : 'name'
      , headerName      : t`First name`
      , editable        : true
      , headerClassName : 'overload-theme--header'
      , flex            : 1 // , minWidth: 100
    }
    , {
      field             : 'surname'
      , headerName      : t`Last name`
      , editable        : true
      , headerClassName : 'overload-theme--header'
      , flex            : 1 // , minWidth: 100
    }
    , {
      field             : 'organization'
      , headerName      : t`Organization`
      , headerClassName : 'overload-theme--header'
      , flex            : 1
      , valueGetter     : value => {
        if (value) {
          return organizations.find(o => o.value === value)?.label
        }
      }
    }
    , {
      field             : 'departmentPol'
      , headerName      : t`Department/Unit`
      , headerClassName : 'overload-theme--header'
      , editable        : true
      , flex            : 1.25 // , minWidth: 125
      , valueGetter     : (_, row) => row.organization === 1 ? row.department : null
      , valueSetter     : (value, row) => ({ ...row, department: value || row.department })
    }
    , {
      field             : 'departmentHos'
      , headerName      : t`Hospital/Healthcare facility`
      , headerClassName : 'overload-theme--header'
      , editable        : true
      , flex            : 1.25 // , minWidth: 125
      , valueGetter     : (_, row) => row.organization === 2 ? row.department : null
      , valueSetter     : (value, row) => ({ ...row, department: value || row.department })
    }
    , {
      field             : 'departmentIns'
      , headerName      : t`Company`
      , headerClassName : 'overload-theme--header'
      , editable        : true
      , flex            : 1.25 // , minWidth: 125
      , valueGetter     : (_, row) => row.organization === 3 ? row.department : null
      , valueSetter     : (value, row) => ({ ...row, department: value || row.department })
    }
    , {
      field             : 'phone'
      , headerName      : t`Phone`
      , editable        : true
      , headerClassName : 'overload-theme--header'
      , flex            : 1 // , minWidth: 100
    }
    , {
      field             : 'email'
      , headerName      : t`Email`
      , editable        : true
      , headerClassName : 'overload-theme--header'
      , flex            : 1 // , minWidth: 100
    }
    , {
      field             : 'create_date'
      , headerName      : t`Date created`
      , headerClassName : 'overload-theme--header'
      , flex            : 1 // , minWidth: 100
    }
    , {
      field             : 'type'
      , headerName      : t`Role`
      , type            : 'singleSelect'
      , editable        : true
      , headerClassName : 'overload-theme--header'
      , flex            : 1 // , minWidth: 100
      , valueOptions    : typeOptions
    }
    , {
      field             : 'verified'
      , headerName      : t`Verified?`
      , type            : 'boolean'
      , editable        : true
      , headerClassName : 'overload-theme--header'
      , flex            : 0.75 // , minWidth: 75
      ,   renderCell: (params) => {
        const isVerified = params.value
        const color = theme.palette.mode === 'dark'
            ? (isVerified ? '#4caf50' : '#f44336')  // verde / rosso in dark
            : (isVerified ? '#2e7d32' : '#d32f2f')  // verde / rosso in light

        return isVerified
            ? <Check sx={{ color }} />
            : <CancelIcon sx={{ color }} />
      }
    }
    , {
      field             : 'actions'
      , type            : 'actions'
      , headerName      : t`Actions`
      , headerClassName : 'overload-theme--header'
      , flex            : 1 // , minWidth: 100
      , cellClassName   : 'actions'
      , getActions      : ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title={t`Save`}><SvgCustomIcon name={'save'}/></Tooltip>}
              label={t`Save`}
              sx={{
                color: 'primary.main'
              }}
              onClick={handleSaveClick(id)}
            />
            , <GridActionsCellItem
              icon={<Tooltip title={t`Cancel`}><SvgCustomIcon name={'cancel'}/></Tooltip>}
              label={t`Cancel`}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ]
        }

        return [
          <GridActionsCellItem
            icon={<Tooltip title={t`Edit`}><SvgCustomIcon color={theme.palette.newColors.gray} name={'edit'} /></Tooltip>}
            label={t`Edit`}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
          , <GridActionsCellItem
            icon={<Tooltip title={t`Change password`}><SvgCustomIcon color={theme.palette.newColors.gray} name={'key'} /></Tooltip>}
            label={t`Change password`}
            className="textPrimary"
            onClick={handleOpenDialog(id)}
            color="inherit"
          />
        ]
      }
    }
  ]

  return (
    <Box
      width='100%'
      sx={theme => ({
        '& .overload-theme--header': {
          backgroundColor   : theme.palette.primary.main
          , color           : theme.palette.primary.contrastText
        }
      })}
    >
      <StripedDataGrid
        showColumnVerticalBorder
        showCellVerticalBorder
        rows={rows}
        columns={columns}
        editMode="row"
        isCellEditable={params => params.row.type !== 1}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={error => error}
        initialState={{ pagination: { paginationModel } }}
        pageSizeOptions={[10, 25, 50]}
        getRowClassName={params => {
          return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }}
      />
      <PasswordChangeDialog id={userId} open={dialogOpen} onClose={handleCloseDialog} />
    </Box>
  )
}
